import React, { useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { useLocation, useHistory, useParams, withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import includes from "lodash/includes";
import orderBy from "lodash/orderBy";
import { useProviderGameTemplateListMutations } from "state/providerGameTemplateState";
import Button from "components/common/Button";
import GreenlineGameSnapshot from "components/games/widgets/GreenlineGameSnapshot";
import ProviderGamesAccordionList from "components/provider/widgets/ProviderGamesAccordionList";
import SortableDataTable from "components/common/SortableDataTable";
import Tooltip from "components/common/Tooltip";
import * as UserPermissions from "constants/UserPermissions";
import { getNameForGameType } from "constants/Games/GameConstants";
import { LEFT } from "constants/TooltipPositionIds";
import { addCommasToNumber } from "utils/numeric";
import pluralizeWord from "utils/pluralizeWord";
import * as ProviderLinks from "utils/ProviderLinks";
import { PermissionTypes, userHasPermission } from "utils/permissions/rolesPermissions";

function ProviderGameInfoTable({
    canViewClinicBIGamesDashboard = false,
    canViewGames = false,
    games = [],
    isTemplates = false,
    search="",
    userId = null,
}) {
    const { pathname } = useLocation();
    const params = useParams();
    const nodeId = Number(params.nodeId);
    const providerId = Number(params.providerId);
    const isDemo = !!includes(pathname, "/demo") && canViewClinicBIGamesDashboard;
    const isAdmin = !!(includes(pathname, "/admin") && nodeId && providerId);
    const history = useHistory();
    const [showModal, setShowModal] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);
    const [rowLoading, setRowLoading] = useState(null);
    const { deleteTemplate, duplicateTemplate } = useProviderGameTemplateListMutations({ userId: userId });

    const handleClose = () => {
        setShowModal(false);
        setSelectedGame(null);
    }

    const handleOpen = (row, e) => {
        e.stopPropagation();
        e.preventDefault();
        setShowModal(true);
        setSelectedGame(row);
    }

    const handleRowClick = (row) => {
        let link;
        if (canViewGames) {
            if (isTemplates) {
                // Handle Template Clicks
                if (userId) {
                    link = ProviderLinks.getLinkLocation("game-user-template", {
                        providerId: providerId,
                        nodeId: nodeId,
                        isAdmin: isAdmin,
                        templateId: row.greenlineGameId,
                        isDemo
                    }, `?userId=${userId}`);
                } else {
                    link = ProviderLinks.getLinkLocation("game-user-template", {
                        providerId: providerId,
                        nodeId: nodeId,
                        isAdmin: isAdmin,
                        templateId: row.greenlineGameId,
                        isDemo
                    });
                }
            } else {
                // Handle Games Clicks
                if (userId) {
                    link = ProviderLinks.getLinkLocation("game", {
                        providerId: providerId,
                        nodeId: nodeId,
                        isAdmin: isAdmin,
                        gameId: row.greenlineGameId,
                        isDemo
                    }, `?userId=${userId}`);
                } else {
                    link = ProviderLinks.getLinkLocation("game", {
                        providerId: providerId,
                        nodeId: nodeId,
                        isAdmin: isAdmin,
                        gameId: row.greenlineGameId,
                        isDemo
                    });
                }
            }
        }
        history.push(link);
    }

    const handleUseTemplate = (e, row) => {
        e.stopPropagation();
        e.preventDefault();
        let link = ProviderLinks.getLinkLocation("game-copy", {
            providerId: providerId,
            nodeId: nodeId,
            isAdmin: isAdmin,
            isDemo: false,
            templateId: row.greenlineGameId
        });
        if (userId) {
            link = ProviderLinks.getLinkLocation("game-copy", {
                providerId: providerId,
                nodeId: nodeId,
                isAdmin: isAdmin,
                isDemo: false,
                templateId: row.greenlineGameId,
            }, `?userId=${userId}`)
        }
        history.push(link);
    }

	const handleDuplicate = async (e, row) => {
		e.stopPropagation();
		e.preventDefault();
        setRowLoading(row.greenlineGameId);
        await duplicateTemplate(row);
        setRowLoading(null);
	};

    const handleDelete = async (e, row) => {
		e.stopPropagation();
		e.preventDefault();
        if (confirm(`Are you sure you want to Delete this template: "${row.name}"?`)) {
            setRowLoading(row.greenlineGameId);
            await deleteTemplate(row.greenlineGameId);
            setRowLoading(null);
        }
	};

    const use_template_column = {
        name: "",
        selector: "use_template",
        key: "use_template",
        format: row => (
            <div>
                <Button
                    disabled={rowLoading === row.greenlineGameId}
                    icon
                    onClick={(e) => handleUseTemplate(e, row)}
                    small
                    type="primary"
                >
                    <i className="fa fa-trophy-alt" /> Use
                </Button>
            </div>
        )
    };

    const game_progress_column = {
        name: "",
        selector: "game_progress",
        key: "game_progress",
        format: row => (canViewGames && row.mostRecentGameSnapshotId)  ? (
            <Button
                title="View Game Progress"
                onClick={(e) => handleOpen(row, e)}
                iconOnly
                text
                type="primary"
            >
                <i style={{ fontSize: "1.75em" }} className="fas fa-chart-line"/>
            </Button>
        ) : "",
    };

    let COLUMNS = [{
        name: "Name",
        selector: "name",
        key: "name",
        searchable: true,
        sortable: true,
        format: row => <div className="no-wrap">{row.name}</div>,
    }, {
        name: "Type",
        selector: "gameType",
        key: "gameType",
        sortable: true,
        format: row => <span className="no-wrap">{getNameForGameType(row.gameType)}</span>,
    }, {
        name: "Clinic(s)",
        selector: "clinicName",
        key: "clinicName",
        format: row => !!row.clinicName ? (
            <span className="no-wrap">{row.clinicName}</span>
        ) : (
            <span className="no-wrap">{addCommasToNumber(row.clinicCount)} {pluralizeWord(row.clinicCount, "clinic")}</span>
        ),
    }, {
        name: "Dates",
        selector: "startDate",
        key: "startDate",
        sortable: true,
        format: row => <span className="no-wrap">{row.startDate} - {row.endDate}</span>,
    }, {
        name: "Duration",
        selector: "description",
        key: "description",
        sortable: true,
        format: row => <div className="no-wrap">{row.description}</div>,
    }, {
        name: "Product Type",
        sortable: true,
        selector: "productType",
        key: "productType",
    }, {
        name: "Product(s)",
        sortable: true,
        selector: "productBrands",
        key: "productBrands",
        format: row => <span>{row.productBrands.join(", ")}</span>
    }];

	const actions_column = {
        name: "",
        selector: "actions",
        key: "actions",
        format: row => (
			<div className="flex flex-end spaced-content">
				<Tooltip
					position={LEFT}
					tip="Duplicte Template"
				>
					<Button
                        disabled={rowLoading === row.greenlineGameId}
						iconOnly
						onClick={(e) => handleDuplicate(e, row)}
                        title="Duplicte Template"
						type="primary"
					>
						<i className="far fa-copy" />
					</Button>
				</Tooltip>
                <Tooltip
					position={LEFT}
					tip="Delete Template"
				>
					<Button
                        disabled={rowLoading === row.greenlineGameId}
						iconOnly
						onClick={(e) => handleDelete(e, row)}
                        title="Delete Template"
						type="danger"
					>
						<i className="fas fa-trash-alt" />
					</Button>
				</Tooltip>
			</div>
        )
    };

    if (!!isTemplates) {
        COLUMNS.unshift(use_template_column);
        COLUMNS.push(actions_column);
    } else {
        COLUMNS.push(game_progress_column);
    }

    return (
        <>
            <SortableDataTable
                allowSearch
                green
                striped
                columns={COLUMNS}
                onClick={(data) => (canViewGames && data.greenlineGameId) ? handleRowClick(data) : {}}
                rawData={orderBy(games, ["greenlineGameId"], ["desc"])}
                search={search}
                hideSearchBar
            />
            {!!selectedGame && (
                <GreenlineGameSnapshot
                    show={showModal}
                    game={selectedGame}
                    handleClose={handleClose}
                    nodeId={nodeId}
                    userId={userId}
                />
            )}
        </>
    );
}

ProviderGamesAccordionList.propTypes = {
    canViewClinicBIGamesDashboard: PropTypes.bool,
    canViewGames: PropTypes.bool,
    games: PropTypes.array,
    isTemplates: PropTypes.bool,
    userId: PropTypes.number,
};

const connector = connect(
    (state) => {
        const userProfile = state.user.userProfile;
        //Permissions
        const canAdminProviderBiGames = userHasPermission(PermissionTypes.ADMIN, UserPermissions.PROVIDER_BI_GAMES, userProfile);
        const canViewProviderBiGames = userHasPermission(PermissionTypes.VIEW, UserPermissions.PROVIDER_BI_GAMES, userProfile);
        const canViewGamesAdmin = userHasPermission(PermissionTypes.VIEW, UserPermissions.GAMES_ADMIN, userProfile);
        const canViewClinicBIGamesDashboard = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_BI_GAMES_DASHBOARD, userProfile);
        const canViewGames = (canAdminProviderBiGames && canViewGamesAdmin) || canViewProviderBiGames;

        return {
            // Permissions
            canViewGames,
            canViewClinicBIGamesDashboard,
        }
    },
);

export default compose(
    withRouter,
    connector
)(ProviderGameInfoTable);
